import Card from "@/components/Card";
import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateToIndonesian, formatRupiah } from "@/lib/utils";
import { Periode, SysProgramStudi } from "@/types";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getPeriode } from "@/services/getData";
import { Calendar, Info, Link as LinkIcon, MoreHorizontal } from "lucide-react";
import { HiMiniArrowLongLeft } from "react-icons/hi2";
import { MdCopyAll, MdSync } from "react-icons/md";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import FormSysBerkas from "../partial/FormSysBerkas";
import FormDayaTampung from "./partial/FormDayaTampung";
import FormJurusanSekolah from "./partial/FormJurusanSekolah";
import FormProdi from "./partial/FormProdi";
import FormCopyMapping from "./partial/FormCopyMapping";

// Helper component untuk Data Item agar rapi
const DataItem = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex flex-col space-y-1 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
    <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
      {label}
    </span>
    <span className="text-sm font-medium text-slate-900 dark:text-slate-200">
      {value || "-"}
    </span>
  </div>
);

export default function SettingPeriodeDetailPage() {
  const [showInsert, setShowInsert] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showUpdateJurusan, setShowUpdateJurusan] = useState(false);
  const [showUpdateBerkas, setShowUpdateBerkas] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDeleteBerkas, setShowDeleteBerkas] = useState(false);
  const [showSyncDayaTampung, setShowSyncDayaTampung] = useState(false);
  const [showCopyMapping, setShowCopyMapping] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SysProgramStudi | null>(null);
  const [selectedIdSysBerkas, setSelectedIdSysBerkas] = useState(0);

  const [searchParams] = useSearchParams();
  const id_sys_periode_params = searchParams.get("id");

  const { data, isPending } = useQuery<Periode>({
    queryKey: ["periode", id_sys_periode_params],
    queryFn: () => getPeriode(id_sys_periode_params as string),
    enabled: !!id_sys_periode_params,
  });

  if (isPending) return <SkeletonTable />;

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-500">
      {/* Header & Back Button */}
      <div className="flex items-center justify-between">
        <Link to={"/admin/setting/periode"}>
          <Button
            variant="ghost"
            className="flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 -ml-2"
          >
            <HiMiniArrowLongLeft className="w-5 h-5" />
            <span>Kembali ke Setting Periode</span>
          </Button>
        </Link>
      </div>

      {/* --- SECTION 1: DATA PERIODE --- */}
      <Card className="p-0 overflow-hidden shadow-sm border-slate-200 dark:border-slate-800">
        <div className="bg-slate-50 dark:bg-slate-900/50 p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
              <Info className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-slate-900 dark:text-white leading-tight">
                Detail Periode
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Informasi utama, jadwal, dan tautan periode pendaftaran.
              </p>
            </div>
          </div>
          <Badge className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-sm rounded-full">
            Tahun {data?.tahun}
          </Badge>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Kelompok Identitas & Biaya */}
          <div className="space-y-4">
            <h3 className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
              Identitas & Biaya
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <DataItem
                label="Jenis Jalur"
                value={data?.jalur_jenis?.nama_jalur_jenis}
              />
              <DataItem label="Gelombang" value={data?.gelombang} />
              <DataItem label="Tahun Ajaran" value={data?.tahun_ajaran_masuk} />
              <DataItem label="Semester" value={data?.semester_masuk} />
              <div className="col-span-2">
                <DataItem
                  label="Jenjang Pendidikan Dibuka"
                  value={data?.jenjang_buka
                    ?.map((j) => j.nama_jenjang_pendidikan)
                    .join(", ")}
                />
              </div>
              <DataItem
                label="Biaya Daftar"
                value={
                  data?.biaya_daftar && formatRupiah(Number(data?.biaya_daftar))
                }
              />
              <DataItem
                label="Biaya Portofolio"
                value={
                  data?.biaya_portofolio &&
                  formatRupiah(Number(data?.biaya_portofolio))
                }
              />
            </div>
          </div>

          {/* Kelompok Jadwal Utama */}
          <div className="space-y-4">
            <h3 className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
              <Calendar className="w-4 h-4 text-slate-400" /> Jadwal Pendaftaran
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <DataItem
                label="Mulai Daftar"
                value={
                  data?.waktu_mulai_daftar
                    ? formatDateToIndonesian(new Date(data.waktu_mulai_daftar))
                    : "N/A"
                }
              />
              <DataItem
                label="Selesai Daftar"
                value={
                  data?.waktu_selesai_daftar
                    ? formatDateToIndonesian(
                      new Date(data.waktu_selesai_daftar),
                    )
                    : "N/A"
                }
              />
              <DataItem
                label="Selesai Daftar Valid"
                value={
                  data?.waktu_selesai_daftar_valid
                    ? formatDateToIndonesian(
                      new Date(data.waktu_selesai_daftar_valid),
                    )
                    : "N/A"
                }
              />
              <DataItem
                label="Pengumuman Lulus"
                value={
                  data?.waktu_pengumuman_kelulusan
                    ? formatDateToIndonesian(
                      new Date(data.waktu_pengumuman_kelulusan),
                    )
                    : "N/A"
                }
              />
              <DataItem
                label="Mulai Sanggah Kelulusan"
                value={
                  data?.waktu_mulai_sanggah_kelulusan
                    ? formatDateToIndonesian(
                      new Date(data.waktu_mulai_sanggah_kelulusan),
                    )
                    : "N/A"
                }
              />
              <DataItem
                label="Selesai Sanggah Kelulusan"
                value={
                  data?.waktu_selesai_sanggah_kelulusan
                    ? formatDateToIndonesian(
                      new Date(data.waktu_selesai_sanggah_kelulusan),
                    )
                    : "N/A"
                }
              />
              {/* <DataItem
                label="Mulai Daftar Kembali"
                value={
                  data?.waktu_mulai_daftarkembali
                    ? formatDateToIndonesian(
                      new Date(data.waktu_mulai_daftarkembali),
                    )
                    : "N/A"
                }
              />
              <DataItem
                label="Selesai Daftar Kembali"
                value={
                  data?.waktu_selesai_daftarkembali
                    ? formatDateToIndonesian(
                      new Date(data.waktu_selesai_daftarkembali),
                    )
                    : "N/A"
                }
              /> */}
              <DataItem
                label="Mulai Kuliah"
                value={
                  data?.tanggal_mulai_kuliah
                    ? formatDateToIndonesian(
                      new Date(data.tanggal_mulai_kuliah),
                    )
                    : "N/A"
                }
              />

            </div>
          </div>

          {/* Kelompok Sanggah & Tautan */}
          <div className="space-y-4 flex flex-col justify-between">
            <div>
              <h3 className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                <Calendar className="w-4 h-4 text-slate-400" /> Jadwal Daftar Kembali
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                <DataItem
                  label="Mulai Isi UKT"
                  value={
                    data?.waktu_mulai_isi_ukt
                      ? formatDateToIndonesian(
                        new Date(data.waktu_mulai_isi_ukt),
                      )
                      : "N/A"
                  }
                />
                <DataItem
                  label="Selesai Isi UKT"
                  value={
                    data?.waktu_selesai_isi_ukt
                      ? formatDateToIndonesian(
                        new Date(data.waktu_selesai_isi_ukt),
                      )
                      : "N/A"
                  }
                />
                <DataItem
                  label="Pengumuman UKT"
                  value={
                    data?.waktu_pengumuman
                      ? formatDateToIndonesian(new Date(data.waktu_pengumuman))
                      : "N/A"
                  }
                />
                <DataItem
                  label="Mulai Sanggah Berkas"
                  value={
                    data?.waktu_mulai_sanggah_berkas_daftarkembali
                      ? formatDateToIndonesian(
                        new Date(
                          data.waktu_mulai_sanggah_berkas_daftarkembali,
                        ),
                      )
                      : "N/A"
                  }
                />
                <DataItem
                  label="Selesai Sanggah Berkas"
                  value={
                    data?.waktu_selesai_sanggah_berkas_daftarkembali
                      ? formatDateToIndonesian(
                        new Date(
                          data.waktu_selesai_sanggah_berkas_daftarkembali,
                        ),
                      )
                      : "N/A"
                  }
                />

              </div>
            </div>

            <div className="mt-4">
              <h3 className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2 mb-3">
                <LinkIcon className="w-4 h-4 text-slate-400" /> Tautan Terkait
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">
                    Grup Telegram Pendaftaran
                  </span>
                  {data?.link_telegram ? (
                    <a
                      href={data.link_telegram}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline break-all"
                    >
                      {data.link_telegram}
                    </a>
                  ) : (
                    <span className="text-sm text-slate-400">-</span>
                  )}
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">
                    Grup Telegram PKKMB
                  </span>
                  {data?.link_telegram_pkkmb ? (
                    <a
                      href={data.link_telegram_pkkmb}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline break-all"
                    >
                      {data.link_telegram_pkkmb}
                    </a>
                  ) : (
                    <span className="text-sm text-slate-400">-</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* --- SECTION 2: LIST PRODI --- */}
      <Card className="p-0 overflow-hidden shadow-sm border-slate-200 dark:border-slate-800">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-slate-950">
          <h2 className="font-bold text-lg text-slate-800 dark:text-slate-100">
            Daftar Program Studi Dibuka
          </h2>
          <div className="flex flex-wrap gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none flex items-center gap-2 border-slate-200 dark:border-slate-700"
              onClick={() => setShowCopyMapping(true)}
            >
              <MdCopyAll className="w-4 h-4" /> Copy Mapping
            </Button>
            <Button
              size="sm"
              className="flex-1 sm:flex-none flex items-center gap-2"
              onClick={() => setShowSyncDayaTampung(true)}
            >
              <MdSync className="w-4 h-4" /> Sync Daya Tampung
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-4 font-semibold w-16">No</th>
                <th className="px-5 py-4 font-semibold">Nama Prodi</th>
                <th className="px-5 py-4 font-semibold text-center">
                  Daya Tampung
                </th>
                <th className="px-5 py-4 font-semibold text-center">Kelas</th>
                <th className="px-5 py-4 font-semibold">
                  List Jurusan Sekolah
                </th>
                <th className="px-5 py-4 font-semibold text-center w-16">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data?.sys_prodi && data?.sys_prodi?.length > 0 ? (
                data.sys_prodi.map((prodi: SysProgramStudi, index: number) => (
                  <tr
                    key={prodi.id_sys_program_studi}
                    className="hover:bg-slate-50/80 dark:hover:bg-slate-900/30 transition-colors"
                  >
                    <td className="px-5 py-4 text-slate-500">{index + 1}</td>
                    <td className="px-5 py-4 font-medium text-slate-900 dark:text-slate-200">
                      {prodi?.unitkerja?.nama}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300">
                        {prodi.daya_tampung}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <Badge
                        variant="outline"
                        className={
                          prodi.is_kelas_denpasar
                            ? "border-blue-200 text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400"
                            : "border-slate-200 text-slate-700 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                        }
                      >
                        {prodi.is_kelas_denpasar ? "Denpasar" : "Singaraja"}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {prodi?.sys_prodi_jurusan_sekolah?.length ? (
                          prodi.sys_prodi_jurusan_sekolah.map(
                            (sysJurusan, idx) => (
                              <Badge
                                key={idx}
                                variant="secondary"
                                className="font-normal dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              >
                                {
                                  sysJurusan.jurusan_sekolah
                                    ?.nama_jurusan_sekolah
                                }
                              </Badge>
                            ),
                          )
                        ) : (
                          <span className="text-xs text-slate-400 italic">
                            Belum diset
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="w-8 h-8 p-0 text-slate-500 hover:text-slate-900 dark:hover:text-white"
                          >
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuLabel className="text-xs text-slate-500 uppercase">
                            Actions
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => {
                              setSelectedRow(prodi);
                              setShowUpdateJurusan(true);
                            }}
                          >
                            Set Jurusan Sekolah
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    Tidak ada data prodi.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* --- SECTION 3: LIST BERKAS --- */}
      <Card className="p-0 overflow-hidden shadow-sm border-slate-200 dark:border-slate-800">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
          <h2 className="font-bold text-lg text-slate-800 dark:text-slate-100">
            Daftar Syarat Berkas
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-4 font-semibold w-16">No</th>
                <th className="px-5 py-4 font-semibold">Jenis Berkas</th>
                <th className="px-5 py-4 font-semibold">Berlaku Untuk Prodi</th>
                <th className="px-5 py-4 font-semibold text-center">Wajib</th>
                <th className="px-5 py-4 font-semibold text-center">
                  Syarat Prodi
                </th>
                <th className="px-5 py-4 font-semibold text-center">Status</th>
                <th className="px-5 py-4 font-semibold">
                  Template / Keterangan
                </th>
                <th className="px-5 py-4 font-semibold text-center w-16">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data?.sys_berkas && data?.sys_berkas?.length > 0 ? (
                data.sys_berkas.map((sysBerkas, index) => (
                  <tr
                    key={sysBerkas.id_sys_berkas}
                    className="hover:bg-slate-50/80 dark:hover:bg-slate-900/30 transition-colors"
                  >
                    <td className="px-5 py-4 text-slate-500">{index + 1}</td>
                    <td className="px-5 py-4 font-medium text-slate-900 dark:text-slate-200">
                      {sysBerkas.jenis_berkas?.nama_jenis_berkas}
                    </td>
                    <td className="px-5 py-4">
                      {sysBerkas?.list_prodi?.length ? (
                        <p
                          className="text-xs text-slate-600 dark:text-slate-300 max-w-[200px] truncate"
                          title={sysBerkas.list_prodi
                            .map((item) => item.unitkerja?.nama)
                            .join(", ")}
                        >
                          {sysBerkas.list_prodi
                            .map((item) => item.unitkerja?.nama)
                            .join(", ")}
                        </p>
                      ) : (
                        <span className="text-xs text-slate-400 italic">
                          Semua Prodi
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-center">
                      {sysBerkas?.is_wajib ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 border-transparent shadow-none">
                          Ya
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-slate-500 dark:border-slate-700"
                        >
                          Tidak
                        </Badge>
                      )}
                    </td>
                    <td className="px-5 py-4 text-center">
                      {sysBerkas?.is_syarat_prodi ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 border-transparent shadow-none">
                          Ya
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-slate-500 dark:border-slate-700"
                        >
                          Tidak
                        </Badge>
                      )}
                    </td>
                    <td className="px-5 py-4 text-center">
                      {sysBerkas?.is_active ? (
                        <Badge className="bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900">
                          Aktif
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-slate-400 dark:border-slate-700"
                        >
                          Nonaktif
                        </Badge>
                      )}
                    </td>
                    <td className="px-5 py-4 max-w-[250px]">
                      <div className="flex flex-col space-y-1">
                        {sysBerkas.url_template_berkas ? (
                          <a
                            href={sysBerkas.url_template_berkas}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1 truncate"
                          >
                            <LinkIcon className="w-3 h-3 flex-shrink-0" /> Lihat
                            Template
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400 italic">
                            Tidak ada template
                          </span>
                        )}
                        {sysBerkas.keterangan_berkas && (
                          <p
                            className="text-xs text-slate-500 dark:text-slate-400 truncate"
                            title={sysBerkas.keterangan_berkas}
                          >
                            Ket: {sysBerkas.keterangan_berkas}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className="w-8 h-8 p-0 text-slate-500 hover:text-slate-900 dark:hover:text-white"
                          >
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-32">
                          <DropdownMenuLabel className="text-xs text-slate-500 uppercase">
                            Actions
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => {
                              setShowUpdateBerkas(true);
                              setSelectedIdSysBerkas(sysBerkas.id_sys_berkas);
                            }}
                          >
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700 dark:focus:bg-red-950/30 dark:text-red-500"
                            onClick={() => {
                              setShowDeleteBerkas(true);
                              setSelectedIdSysBerkas(sysBerkas.id_sys_berkas);
                            }}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    Tidak ada data berkas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* --- MODALS --- */}
      {data && (
        <>
          <Modal show={showInsert} onClose={() => setShowInsert(false)}>
            <FormProdi method="POST" closeModal={() => setShowInsert(false)} />
          </Modal>
          <Modal show={showUpdate} onClose={() => setShowUpdate(false)}>
            <FormProdi
              selectedRow={selectedRow}
              method="PATCH"
              closeModal={() => setShowUpdate(false)}
            />
          </Modal>
          <Modal
            show={showUpdateJurusan}
            onClose={() => setShowUpdateJurusan(false)}
            className="lg:w-[70vw] w-[95vw]"
          >
            <FormJurusanSekolah
              selectedRow={selectedRow}
              closeModal={() => setShowUpdateJurusan(false)}
            />
          </Modal>
          <Modal show={showDelete} onClose={() => setShowDelete(false)}>
            <FormProdi
              selectedRow={selectedRow}
              method="DELETE"
              closeModal={() => setShowDelete(false)}
            />
          </Modal>
        </>
      )}

      <Modal
        show={showUpdateBerkas}
        onClose={() => setShowUpdateBerkas(false)}
        className="lg:w-[80vw] w-[95vw]"
      >
        <FormSysBerkas
          method="PATCH"
          closeModal={() => setShowUpdateBerkas(false)}
          selectedRow={data}
          selectedIdSysBerkas={selectedIdSysBerkas}
        />
      </Modal>

      <Modal show={showDeleteBerkas} onClose={() => setShowDeleteBerkas(false)}>
        <FormSysBerkas
          method="DELETE"
          closeModal={() => setShowDeleteBerkas(false)}
          selectedRow={data}
          selectedIdSysBerkas={selectedIdSysBerkas}
        />
      </Modal>

      <Modal
        show={showSyncDayaTampung}
        onClose={() => setShowSyncDayaTampung(false)}
      >
        <FormDayaTampung closeModal={() => setShowSyncDayaTampung(false)} />
      </Modal>

      <Modal show={showCopyMapping} onClose={() => setShowCopyMapping(false)}>
        <FormCopyMapping closeModal={() => setShowCopyMapping(false)} />
      </Modal>
    </div>
  );
}
